/**
 * Quand une requête vaut la peine d'être rejouée (#1682).
 *
 * Le client rejouait **tout**, deux fois, à trois secondes d'intervalle — sans regarder le statut.
 * Un 4xx est un refus définitif : le rejouer ne peut pas le changer, et coûte trois fois.
 *
 * - **429** : le limiteur vient de dire « trop de demandes », et le client en envoyait deux de plus.
 *   C'est le geste exact que le limiteur cherche à empêcher, au moment précis où la charge pose
 *   problème. Réessayer se propose à l'écran — le serveur dit désormais dans quel délai
 *   (suivora-api#1276) —, ça ne se fait pas dans le dos de la personne.
 * - **403** : la permission ne sera pas accordée dans les six secondes qui suivent.
 * - **404**, **409**, **422** : la réponse ne dépend pas du moment où on la demande.
 *
 * Et l'écran mettait **neuf secondes** à annoncer un échec connu dès la première réponse.
 */

/** Nombre de reprises pour ce qui peut encore réussir. */
export const MAX_RETRIES = 2;

/**
 * `408 Request Timeout` est le seul 4xx qui se rejoue.
 *
 * Ce n'est pas un refus : la requête n'a pas abouti dans le temps imparti, et la suivante le peut.
 * Le ranger avec les refus reviendrait à traiter une lenteur passagère comme une décision.
 */
const RETRYABLE_CLIENT_STATUSES = new Set([408]);

/** Le statut porté par l'erreur du client HTTP — absent sur une panne réseau. */
const statusOf = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * `true` s'il faut retenter.
 *
 * **Une erreur sans statut est une panne réseau**, pas un refus : coupure de wifi, requête annulée,
 * serveur injoignable. C'est précisément le cas où retenter marche, et le traiter comme un refus
 * ferait échouer un écran qu'une seconde tentative aurait affiché.
 */
export function shouldRetryRequest(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;

  const status = statusOf(error);
  if (status === undefined) return true;
  if (status >= 400 && status < 500) return RETRYABLE_CLIENT_STATUSES.has(status);

  return true;
}

/**
 * L'attente avant la reprise, **croissante**.
 *
 * Elle était fixe à trois secondes : deux reprises tombaient dans la même fenêtre d'incident, et
 * repartaient donc contre un serveur encore dans l'état qui vient de les refuser. La progression
 * exponentielle est le comportement par défaut de la bibliothèque — il avait été remplacé par une
 * constante.
 */
export const retryDelayMs = (attemptIndex: number): number =>
  Math.min(1_000 * 2 ** attemptIndex, 15_000);
